import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import './Login.css'; // Import the CSS file for styling
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@fortawesome/free-solid-svg-icons';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [checkingSession, setCheckingSession] = useState(true);
    const [resetMode, setResetMode] = useState(false);
    const [resetSent, setResetSent] = useState(false);
    const [sessionResetStatus, setSessionResetStatus] = useState('');
    
    const { login, resetPassword, resetSession, auth, loading: authLoading, sessionValid } = useAuth();
    const navigate = useNavigate();

    // Check for existing session on component mount
    useEffect(() => {
        const checkExistingSession = async () => {
            try {
                console.log('Checking for existing session on login page...');
                
                // Wait for auth context to finish loading
                if (authLoading) {
                    console.log('Auth context still loading...');
                    return;
                }
                
                console.log('Auth state:', { auth, sessionValid });
                
                if (auth && sessionValid) {
                    console.log('Valid session found, redirecting to dashboard');
                    navigate('/dashboard');
                } else {
                    console.log('No valid session found, staying on login page');
                    setCheckingSession(false);
                }
            } catch (error) {
                console.error('Error checking session:', error);
                setCheckingSession(false);
            }
        };
        
        checkExistingSession();
    }, [auth, navigate, authLoading, sessionValid]);

    // Update checking session state when auth loading changes
    useEffect(() => {
        if (!authLoading) {
            setCheckingSession(false);
        }
    }, [authLoading]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        
        try {
            console.log('Attempting login with:', email);
            
            // Call the login function from AuthContext
            const { success, error } = await login(email, password);
            
            if (success) {
                console.log('Login successful, redirecting to dashboard...');
                navigate('/dashboard');
            } else {
                setError(error?.message || 'Failed to login. Please check your credentials.');
                console.error('Login failed:', error);
                setLoading(false);
            }
        } catch (err) {
            setError('An unexpected error occurred. Please try again.');
            console.error('Login error:', err);
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (!email) {
            setError('Please enter your email address to reset your password.');
            return;
        }
        
        setError('');
        setLoading(true);
        
        try {
            console.log('Requesting password reset for:', email);
            const { success, error } = await resetPassword(email);
            
            if (success) {
                setResetSent(true);
                console.log('Password reset email sent successfully');
            } else {
                setError(error?.message || 'Failed to send reset email. Please try again.');
                console.error('Password reset failed:', error);
            }
        } catch (err) {
            setError('An unexpected error occurred. Please try again.');
            console.error('Password reset error:', err);
        } finally {
            setLoading(false);
        }
    };

    const toggleResetMode = () => {
        setResetMode(!resetMode);
        setError('');
        setResetSent(false);
    };

    const handleSessionReset = async () => {
        setSessionResetStatus('Resetting session data...');
        setLoading(true);
        
        try {
            const result = await resetSession();
            setSessionResetStatus(result.message + '. Please try logging in again.');
            
            setTimeout(() => {
                window.location.reload();
            }, 2000);
        } catch (err) {
            console.error('Error resetting session:', err);
            setSessionResetStatus('Error resetting session. Please try again or reload the page.');
        } finally {
            setLoading(false);
        }
    };

    if (checkingSession || authLoading) {
        return (
            <div className="form-container">
                <h2>Checking Session</h2>
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Checking for existing session...</p>
                </div>
            </div>
        );
    }

    if (resetMode) {
        return (
            <div className="form-container">
                <h2>Reset Password</h2>
                {error && <div className="error-message">{error}</div>}
                
                {resetSent ? (
                    <div className="success-message">
                        Password reset email has been sent. Please check your inbox and follow the instructions.
                        <button className="text-button" onClick={toggleResetMode}>
                            Return to Login
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleResetPassword}>
                        <div className="form-group">
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                        
                        <button 
                            type="submit" 
                            className="submit-button"
                            disabled={loading}
                        >
                            {loading ? 'Sending...' : 'Send Reset Link'}
                        </button>
                        
                        <div className="form-footer">
                            <button 
                                type="button" 
                                className="text-button"
                                onClick={toggleResetMode}
                            >
                                Back to Login
                            </button>
                        </div>
                    </form>
                )}
                
                <div className="session-reset-container">
                    <p>Having trouble logging in?</p>
                    <button 
                        className="reset-session-button" 
                        onClick={handleSessionReset}
                        disabled={loading || sessionResetStatus.includes('Resetting')}
                    >
                        <FontAwesomeIcon icon={faSync} spin={sessionResetStatus.includes('Resetting')} />
                        Reset Session Data
                    </button>
                    {sessionResetStatus && <div className="info-message">{sessionResetStatus}</div>}
                </div>
            </div>
        );
    }

    return (
        <div className="form-container">
            <h2>Login</h2>
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                </div>
                
                <div className="form-group">
                    <label htmlFor="password">Password</label>
                    <input
                        type="password"
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                    />
                </div>
                
                <button 
                    type="submit" 
                    className="submit-button"
                    disabled={loading}
                >
                    {loading ? 'Logging in...' : 'Login'}
                </button>
                
                <div className="form-footer">
                    <button 
                        type="button" 
                        className="text-button"
                        onClick={toggleResetMode}
                    >
                        Forgot Password?
                    </button>
                    
                    <p>
                        Don't have an account? <Link to="/register">Register</Link>
                    </p>
                </div>
            </form>
            
            <div className="session-reset-container">
                <p>Having trouble logging in?</p>
                <button 
                    className="reset-session-button" 
                    onClick={handleSessionReset}
                    disabled={loading || sessionResetStatus.includes('Resetting')}
                >
                    <FontAwesomeIcon icon={faSync} spin={sessionResetStatus.includes('Resetting')} />
                    Reset Session Data
                </button>
                {sessionResetStatus && <div className="info-message">{sessionResetStatus}</div>}
            </div>
        </div>
    );
};

export default Login;
